import { getCountries, getCountryCallingCode, CountryCode } from "libphonenumber-js/max";
import { Metadata, NumberingPlan } from "libphonenumber-js/core";
import metadataMax from "libphonenumber-js/metadata.max.json";
import getPrefixFromUuid from "../getPrefixFromUuid";
import { VALIDATION_ENUM } from "enums";

export interface PhoneValidationResult {
  isValid: boolean;
  error?: string;
}

// `NumberingPlan.type()` isn't part of libphonenumber-js's published .d.ts,
// but the library ships it in the full ("max") metadata and relies on it
// internally for isValid()/getType() — this just gives it a local type.
interface NumberingPlanWithTypes extends NumberingPlan {
  type(type: "MOBILE"): { possibleLengths(): number[] } | undefined;
}

const getMobileNumberLengths = (
  isoCountry: CountryCode
): number[] | undefined => {
  const metadata = new Metadata(metadataMax);
  metadata.selectNumberingPlan(isoCountry);
  return (
    metadata.numberingPlan as NumberingPlanWithTypes | undefined
  )?.type("MOBILE")?.possibleLengths();
};

const formatDigitsList = (lengths: number[]): string => {
  const unique = Array.from(new Set(lengths)).sort((a, b) => a - b);
  if (unique.length === 1) {
    return `${unique[0]} znaków`;
  }
  const last = unique[unique.length - 1];
  const rest = unique.slice(0, -1).join(", ");
  return `${rest} lub ${last} znaków`;
};

/**
 * Validates a phone number's length for the selected country's mobile numbers.
 * Only checks digit count — not whether the number is an actually assigned/valid number.
 * @param phoneNumber The raw phone number string
 * @param phonePrefixUuid The UUID of the phone prefix (country code)
 * @param prefixes Array of available prefixes from the API
 * @returns An object with validation result and optional error message
 */
export const validatePhoneNumber = (
  phoneNumber: string,
  phonePrefixUuid: string,
  prefixes: Array<{ value: string; label: string }>
): PhoneValidationResult => {
  // If any required parameter is missing, return invalid
  if (!phoneNumber || !phonePrefixUuid || !prefixes.length) {
    return { isValid: false, error: VALIDATION_ENUM.REQUIRED };
  }

  // Get the prefix (country code) from UUID
  const prefix = getPrefixFromUuid(phonePrefixUuid, prefixes);
  if (!prefix) {
    return { isValid: false, error: VALIDATION_ENUM.REQUIRED };
  }

  // Remove the '+' from the prefix to get just the country code
  const countryCode = prefix.replace("+", "");

  const isoCountry = getCountries().find(
    (country) => getCountryCallingCode(country) === countryCode
  );
  const mobileLengths = isoCountry
    ? getMobileNumberLengths(isoCountry)
    : undefined;

  if (mobileLengths?.length && !mobileLengths.includes(phoneNumber.length)) {
    return {
      isValid: false,
      error: `Numer telefonu musi mieć ${formatDigitsList(mobileLengths)}`,
    };
  }

  return { isValid: true };
};
